import { CloudUpload } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button } from '@mui/material';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useReUploadDataSource } from '~/api/data-sources';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { useAppContext } from '~/contexts';
import { DataSourceKind } from '~/generated/graphql';
import { usePaginationParams } from '~/hooks/pagination';
import { toDataUri } from '~/lib/file';
import { DataSourceDataDocument } from '../../queries/data.generated';
import type { DataSourceLayout__DataSource as DataSource } from '../../queries/layout.generated';

interface UploadButtonProps {
  dataSource: DataSource;
}

export const UploadButton = ({ dataSource }: UploadButtonProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [reUploadDataSource, { loading }] = useReUploadDataSource();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { page, perPage } = usePaginationParams();
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { getInputProps, open: selectFile } = useDropzone({
    accept:
      dataSource.kind === DataSourceKind.Csv
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    multiple: false,
    onDrop: async (accepted, _rejected, _event) => {
      if (accepted.length !== 1) return;
      const dataUri = await toDataUri(accepted[0]);
      try {
        await reUploadDataSource({
          variables: {
            input: {
              dataUri,
              dataSourceId: dataSource.id,
              networkId: currentNetwork.id,
              name: dataSource.name,
            },
            page,
            perPage,
            search,
          },
          refetchQueries: [DataSourceDataDocument],
        });
        setIsDialogOpen(false);
      } catch (error) {
        console.error('Failed to re-upload data source:', error);
      }
    },
  });

  const getDialogTitle = () => {
    return dataSource.foreignIdName
      ? t('pages.data_sources.upload_dialog.title_update')
      : t('pages.data_sources.upload_dialog.title_reupload');
  };

  const getDialogPrompt = () => {
    return dataSource.foreignIdName
      ? t('pages.data_sources.upload_dialog.prompt_with_id', {
          foreignIdName: dataSource.foreignIdName,
        })
      : t('pages.data_sources.upload_dialog.prompt_without_id');
  };

  const handleCancel = () => {
    if (!loading) setIsDialogOpen(false);
  };

  return (
    <>
      <input {...getInputProps()} />
      <Button
        disabled={!dataSource.canUpdateItems.value}
        color="primary"
        startIcon={<CloudUpload />}
        variant="contained"
        onClick={() => setIsDialogOpen(true)}
      >
        {t('common.buttons.upload')}
      </Button>
      <ConfirmDialog
        open={isDialogOpen}
        onCancel={handleCancel}
        onConfirm={selectFile}
        confirmButton={
          <LoadingButton
            loading={loading}
            onClick={selectFile}
            startIcon={<CloudUpload />}
            variant="contained"
          >
            {t('common.buttons.upload')}
          </LoadingButton>
        }
        prompt={getDialogPrompt()}
        title={getDialogTitle()}
      />
    </>
  );
};
